/// ElementPalette: Categorized, searchable element palette.
/// Shows BPMN or Node elements based on active editor type.
/// Supports search filtering, click-to-add, and drag-to-canvas.

import { useState, useMemo } from "react";
import {
  Circle,
  CircleDot,
  Clock,
  Mail,
  Radio,
  AlertTriangle,
  XCircle,
  Square,
  Settings,
  User,
  Code,
  Send,
  Inbox,
  Diamond,
  DiamondPlus,
  Box,
  ExternalLink,
  Columns2,
  Minus,
  Plus,
  X,
  Divide,
  Percent,
  Hash,
  Dice1,
  TextCursorInput,
  Scissors,
  Replace,
  AlignLeft,
  Ruler,
  FileText,
  GitBranch,
  GitMerge,
  Scale,
  Ban,
  Variable,
  Lock,
  Braces,
  FileJson,
  Key,
  PenLine,
  Play,
  Timer,
  Repeat,
  Globe,
  ArrowDown,
  List,
  Filter,
  Minimize,
  ArrowUpDown,
  Layers,
  GripVertical,
  type LucideIcon,
} from "lucide-react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { useEditorStore } from "@/stores/editorStore";
import {
  bpmnPaletteCategories,
  type PaletteItemDef,
  type PaletteCategory,
} from "./palette/BPMNPaletteItems";
import { nodePaletteCategories } from "./palette/NodePaletteItems";

// ---------------------------------------------------------------------------
// Icon map from string name to Lucide component
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, LucideIcon> = {
  circle: Circle,
  "circle-dot": CircleDot,
  clock: Clock,
  mail: Mail,
  radio: Radio,
  "alert-triangle": AlertTriangle,
  "x-circle": XCircle,
  square: Square,
  settings: Settings,
  user: User,
  code: Code,
  send: Send,
  inbox: Inbox,
  diamond: Diamond,
  "diamond-plus": DiamondPlus,
  box: Box,
  "external-link": ExternalLink,
  columns: Columns2,
  minus: Minus,
  plus: Plus,
  x: X,
  divide: Divide,
  percent: Percent,
  hash: Hash,
  "dice-1": Dice1,
  "text-cursor-input": TextCursorInput,
  scissors: Scissors,
  replace: Replace,
  "align-left": AlignLeft,
  ruler: Ruler,
  "file-text": FileText,
  "git-branch": GitBranch,
  "git-merge": GitMerge,
  scale: Scale,
  ban: Ban,
  variable: Variable,
  lock: Lock,
  braces: Braces,
  "file-json": FileJson,
  key: Key,
  "pen-line": PenLine,
  play: Play,
  timer: Timer,
  repeat: Repeat,
  globe: Globe,
  "arrow-down": ArrowDown,
  list: List,
  filter: Filter,
  minimize: Minimize,
  "arrow-up-down": ArrowUpDown,
  layers: Layers,
  iterate: Repeat, // alias
  "git-fork": GitBranch, // alias
};

// Color configuration with intensity variants for hover/active states
const COLOR_VARIANTS: Record<string, { normal: string; hover: string; active: string }> = {
  green: {
    normal: "text-green-400",
    hover: "group-hover:text-green-300",
    active: "group-active:text-green-500",
  },
  blue: {
    normal: "text-blue-400",
    hover: "group-hover:text-blue-300",
    active: "group-active:text-blue-500",
  },
  orange: {
    normal: "text-orange-400",
    hover: "group-hover:text-orange-300",
    active: "group-active:text-orange-500",
  },
  purple: {
    normal: "text-purple-400",
    hover: "group-hover:text-purple-300",
    active: "group-active:text-purple-500",
  },
  red: {
    normal: "text-red-400",
    hover: "group-hover:text-red-300",
    active: "group-active:text-red-500",
  },
  cyan: {
    normal: "text-cyan-400",
    hover: "group-hover:text-cyan-300",
    active: "group-active:text-cyan-500",
  },
  zinc: {
    normal: "text-zinc-400",
    hover: "group-hover:text-zinc-300",
    active: "group-active:text-zinc-500",
  },
  amber: {
    normal: "text-amber-400",
    hover: "group-hover:text-amber-300",
    active: "group-active:text-amber-500",
  },
};

function PaletteIcon({ name, color, size = 14 }: { name: string; color: string; size?: number }) {
  const IconComponent = ICON_MAP[name];
  if (!IconComponent) {
    return <GripVertical size={size} className="text-zinc-500" />;
  }

  // Get color variant classes for normal/hover/active states
  const variant = COLOR_VARIANTS[color] || COLOR_VARIANTS.zinc;
  const colorClass = `${variant.normal} ${variant.hover} ${variant.active} transition-colors`;

  return <IconComponent size={size} className={colorClass} />;
}

// ---------------------------------------------------------------------------
// PaletteItem component
// ---------------------------------------------------------------------------

function PaletteItem({
  item,
  color,
  onDragStart,
  onClick,
}: {
  item: PaletteItemDef;
  color: string;
  onDragStart: (e: React.DragEvent) => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="group flex items-center gap-2 rounded px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 cursor-grab active:cursor-grabbing transition-colors"
    >
      <PaletteIcon name={item.icon} color={color} size={14} />
      <span className="truncate">{item.label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ElementPalette component
// ---------------------------------------------------------------------------

export function ElementPalette() {
  const tabs = useEditorStore((s) => s.tabs);
  const activeTabId = useEditorStore((s) => s.activeTabId);
  const pendingConnectSource = useEditorStore((s) => s.pendingConnectSource);
  const setPendingConnectSource = useEditorStore((s) => s.setPendingConnectSource);
  const setConnectionHint = useEditorStore((s) => s.setConnectionHint);
  const [search, setSearch] = useState("");

  const activeTab = useMemo(() => tabs.find((t) => t.id === activeTabId), [tabs, activeTabId]);

  const categories: PaletteCategory[] =
    activeTab?.editorType === "nodeflow" ? nodePaletteCategories : bpmnPaletteCategories;

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return categories;

    const lowerSearch = search.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.label.toLowerCase().includes(lowerSearch) ||
            item.type.toLowerCase().includes(lowerSearch),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [categories, search]);

  const handleDragStart = (item: PaletteItemDef) => (e: React.DragEvent) => {
    const editorType = activeTab?.editorType ?? "bpmn";
    const mimeType =
      editorType === "bpmn" ? "application/flowy-bpmn-element" : "application/flowy-node-element";
    e.dataTransfer.setData(mimeType, JSON.stringify(item));
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleClick = (item: PaletteItemDef) => () => {
    // If there is a pending connect source (empty canvas drop), dispatch createAndConnect
    if (pendingConnectSource) {
      window.dispatchEvent(
        new CustomEvent("flowy:createAndConnect", {
          detail: {
            elementType: item.type,
            label: item.label,
            x: pendingConnectSource.dropX,
            y: pendingConnectSource.dropY,
            sourceId: pendingConnectSource.sourceId,
          },
        }),
      );
      setPendingConnectSource(null);
      setConnectionHint("Connection created -- Click next source element");
      return;
    }

    // Normal: add element at center of viewport via CustomEvent
    const event = new CustomEvent("flowy:addElement", {
      detail: {
        type: item.type,
        label: item.label,
        position: "center", // Editor worker resolves to viewport center
      },
    });
    window.dispatchEvent(event);
  };

  if (!activeTab) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-zinc-500">
        Open an editor to see elements
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search input */}
      <div className="p-2 border-b border-zinc-700">
        <input
          type="text"
          placeholder="Search elements..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Categorized list */}
      <ScrollArea.Root className="flex-1 min-h-0">
        <ScrollArea.Viewport className="h-full w-full">
          {filtered.length === 0 && (
            <div className="flex items-center justify-center py-8 text-xs text-zinc-500">
              No elements match "{search}"
            </div>
          )}
          {filtered.map((category) => (
            <div key={category.name}>
              <div className="sticky top-0 bg-zinc-800 px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider z-[1]">
                {category.name}
              </div>
              <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                {category.items.map((item) => (
                  <PaletteItem
                    key={item.type}
                    item={item}
                    color={category.color}
                    onDragStart={handleDragStart(item)}
                    onClick={handleClick(item)}
                  />
                ))}
              </div>
            </div>
          ))}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex touch-none select-none bg-zinc-800 p-0.5 transition-colors hover:bg-zinc-700 data-[orientation=vertical]:w-2"
        >
          <ScrollArea.Thumb className="relative flex-1 rounded-full bg-zinc-600" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </div>
  );
}
